import { useEffect, useRef, useState } from "react";
import { labelWorkOrderFieldsSchema } from "../../../shared/labels/work-order-request-contracts";
import type { LabelWorkOrderLookupResponse, LabelWorkOrderReviewResponse, LabelWorkOrderCreateResult, LabelWorkOrderSelection, labelWorkOrderCreateRequestSchema } from "../../../shared/labels/work-order-request-contracts";
import type { z } from "zod";
import type { RequestDetail, RequestList } from "../../../shared/labels/contracts";
import { initialQuery } from "./ui-types";
import { uiFault, type LabelsApi } from "./labels-api";

export const initialWorkOrderFields = { variantKey: "", labelKind: "", packaging: "", language: "", customerRequest: "", quantity: "", dueAt: "" };
export type WorkOrderFieldsDraft = typeof initialWorkOrderFields;
type CreateInput = z.infer<typeof labelWorkOrderCreateRequestSchema>;

export function workOrderFieldsInput(draft: WorkOrderFieldsDraft) {
  const date = draft.dueAt ? new Date(draft.dueAt) : null;
  return labelWorkOrderFieldsSchema.safeParse({
    variantKey: draft.variantKey, labelKind: draft.labelKind, packaging: draft.packaging,
    language: draft.language, customerRequest: draft.customerRequest,
    requestedPrintQuantity: draft.quantity.trim() ? Number(draft.quantity) : null,
    dueAt: date && Number.isFinite(date.getTime()) ? date.toISOString() : draft.dueAt ? "invalid" : null,
  });
}

export function useWorkOrderRequestWorkspace(api: LabelsApi, scopeKey: string) {
  const [workOrder, setWorkOrder] = useState("");
  const [lookup, setLookup] = useState<LabelWorkOrderLookupResponse | null>(null);
  const [selection, setSelection] = useState<LabelWorkOrderSelection | null>(null);
  const [fields, setFields] = useState(initialWorkOrderFields);
  const [review, setReview] = useState<LabelWorkOrderReviewResponse | null>(null);
  const [result, setResult] = useState<LabelWorkOrderCreateResult | null>(null);
  const [fault, setFault] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const attempt = useRef<{ body: CreateInput; key: string } | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [page, setPage] = useState(1);
  const [mine, setMine] = useState<RequestList | null>(null);
  const [mineError, setMineError] = useState<string | null>(null);
  const [mineLoading, setMineLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    setLookup(null); setSelection(null); setFields(initialWorkOrderFields); setReview(null); setResult(null);
    setWorkOrder(""); setPage(1); setBusy(false); setSelectedId(null); setFault(null); setUncertain(false); attempt.current = null;
    return () => { controller.current?.abort(); busyRef.current = false; };
  }, [api, scopeKey]);
  useEffect(() => {
    const request = new AbortController();
    setMineLoading(true); setMineError(null); setMine(null);
    void api.ownWorkOrderRequests({ ...initialQuery, page, pageSize: 20 }, request.signal).then(data => { if (!request.signal.aborted) setMine(data); })
      .catch((error: unknown) => { if (!request.signal.aborted) setMineError(uiFault(error).message); })
      .finally(() => { if (!request.signal.aborted) setMineLoading(false); });
    return () => request.abort();
  }, [api, scopeKey, reload, page]);
  useEffect(() => {
    const request = new AbortController(); setDetail(null); setDetailError(null);
    if (selectedId) void api.detail(selectedId, request.signal).then(data => { if (!request.signal.aborted) setDetail(data); })
      .catch((error: unknown) => { if (!request.signal.aborted) setDetailError(uiFault(error).message); });
    return () => request.abort();
  }, [api, scopeKey, selectedId, reload]);
  const dirty = !result && Boolean(workOrder || Object.values(fields).some(Boolean));
  useEffect(() => {
    if (!dirty) return;
    const prevent = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", prevent);
    return () => window.removeEventListener("beforeunload", prevent);
  }, [dirty]);

  async function run(action: (signal: AbortSignal) => Promise<void>, create = false) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setFault(null);
    const request = new AbortController(); controller.current = request;
    try { await action(request.signal); }
    catch (error: unknown) {
      if (!request.signal.aborted) {
        const failure = uiFault(error); setFault(failure.message);
        if (create) {
          const ambiguous = ["NETWORK", "TIMEOUT", "INVALID_RESPONSE"].includes(failure.code) || (failure.status ?? 0) >= 500;
          setUncertain(ambiguous);
          if (!ambiguous) attempt.current = null;
        }
      }
    } finally {
      if (!request.signal.aborted) { busyRef.current = false; setBusy(false); }
    }
  }
  function changeWorkOrder(value: string) {
    if (busyRef.current || uncertain) return;
    setWorkOrder(value); setFields(initialWorkOrderFields); setLookup(null); setSelection(null); setReview(null); setResult(null); setFault(null); attempt.current = null;
  }
  function changeField(key: keyof WorkOrderFieldsDraft, value: string) {
    if (busyRef.current || uncertain) return;
    setFields(current => ({ ...current, [key]: value })); setReview(null); setResult(null); setFault(null); attempt.current = null;
  }
  const lookupOrder = () => run(async signal => {
    setLookup(null); setSelection(null); setReview(null); setResult(null);
    const data = await api.lookupWorkOrder(workOrder.trim(), signal);
    if (!signal.aborted) { setLookup(data); if (data.options.length === 1) setSelection(data.options[0] ?? null); }
  });
  const reviewOrder = () => {
    const parsed = workOrderFieldsInput(fields);
    if (!parsed.success) { setFault("Etiket türü, dil, varyant, ambalaj ve pozitif tam sayı baskı adedini kontrol edin."); return; }
    if (!lookup || !selection) return;
    void run(async signal => {
      const data = await api.reviewWorkOrder({ lookupToken: lookup.lookupToken, selectionToken: selection.selectionToken, labelFields: parsed.data }, signal);
      if (!signal.aborted) { setReview(data); setUncertain(false); attempt.current = null; }
    });
  };
  const save = (resolution: CreateInput["resolution"]) => {
    if (!review || review.blockers.some(item => item.severity === "blocking")) return;
    if (!attempt.current) attempt.current = { body: { reviewToken: review.reviewToken, resolution }, key: crypto.randomUUID() };
    const command = attempt.current;
    void run(async signal => {
      const data = await api.createWorkOrder(command.body, command.key, signal);
      if (!signal.aborted) { setResult(data); setPage(1); setUncertain(false); attempt.current = null; setReload(value => value + 1); }
    }, true);
  };
  function reset() {
    if (busyRef.current || uncertain) return;
    setWorkOrder(""); setLookup(null); setSelection(null); setFields(initialWorkOrderFields); setReview(null); setResult(null); setFault(null); attempt.current = null;
  }
  return { workOrder, changeWorkOrder, lookup, lookupOrder, selection,
    select: (value: LabelWorkOrderSelection) => { if (!busyRef.current && !uncertain) { setSelection(value); setFields(initialWorkOrderFields); setReview(null); setResult(null); attempt.current = null; } },
    fields, changeField, review, reviewOrder, result, fault, busy, uncertain, save, reset,
    mine, mineError, mineLoading, page, setPage, reloadMine: () => setReload(value => value + 1),
    detail, detailError, selectedId, selectRequest: setSelectedId };
}
